<template>
    <Button
        v-if="isSupported"
        @click="handleCopy"
        :variant="isCopied ? 'ghost' : variant"
        :size="size"
        :class="
            cn(
                'transition-colors duration-300',
                isCopied ? 'text-emerald-500 hover:text-emerald-500' : '',
                props.class,
            )
        ">
        <slot v-if="!isCopied">
            <CopyIcon v-if="iconOnly" class="size-3.5" />
            <span v-else>Copy</span>
        </slot>
        <slot name="copied" v-else>
            <CheckIcon v-if="iconOnly" class="size-3.5" />
            <span v-else>Copied</span>
        </slot>
    </Button>
</template>

<script setup lang="ts">
import { ref, type HTMLAttributes } from 'vue'
import { useClipboard, useTimeoutFn } from '@vueuse/core'
import { Button } from '@/components/ui/button'
import { CopyIcon, CheckIcon } from 'lucide-vue-next'
import type { ButtonVariants } from '@/components/ui/button'
import { cn } from '@/lib/utils'

interface Props {
    textToCopy: string
    variant?: ButtonVariants['variant']
    size?: ButtonVariants['size']
    iconOnly?: boolean // Add prop to determine if only icon should be shown
    class?: HTMLAttributes['class']
}

const props = withDefaults(defineProps<Props>(), {
    variant: 'ghost',
    size: undefined,
    iconOnly: false,
})

const { copy, isSupported } = useClipboard({ source: props.textToCopy })
const isCopied = ref(false)

const { start: startResetTimer } = useTimeoutFn(() => {
    isCopied.value = false
}, 3000)

const handleCopy = () => {
    copy()
    isCopied.value = true
    startResetTimer()
}
</script>
